import { Box, Link } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

type ChatMessageMarkdownProps = {
  content: string;
};

const baseMarkdownSx = {
  fontSize: { xs: '11px', sm: '11.5px', md: '12px' },
  lineHeight: 1.55,
  color: 'inherit',
  '& p': {
    margin: 0,
    marginBottom: 0.75,
    '&:last-child': { marginBottom: 0 },
  },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    fontWeight: 700,
    marginTop: 0.75,
    marginBottom: 0.35,
    lineHeight: 1.25,
    '&:first-of-type': { marginTop: 0 },
  },
  '& h1': {
    fontSize: { xs: '0.9rem', sm: '0.98rem', md: '1.05rem', lg: '1.12rem' },
  },
  '& h2': {
    fontSize: { xs: '0.84rem', sm: '0.92rem', md: '0.98rem', lg: '1.04rem' },
  },
  '& h3': {
    fontSize: { xs: '0.78rem', sm: '0.86rem', md: '0.92rem', lg: '0.98rem' },
  },
  '& h4': {
    fontSize: { xs: '0.74rem', sm: '0.8rem', md: '0.86rem', lg: '0.92rem' },
  },
  '& h5': {
    fontSize: { xs: '0.7rem', sm: '0.76rem', md: '0.8rem', lg: '0.86rem' },
  },
  '& h6': {
    fontSize: { xs: '0.66rem', sm: '0.72rem', md: '0.76rem', lg: '0.82rem' },
  },
  '& ul, & ol': {
    margin: '0.35em 0',
    paddingLeft: '1.35em',
  },
  '& li': { marginBottom: 0.25 },
  '& blockquote': {
    margin: '0.5em 0',
    paddingLeft: '0.75em',
    borderLeft: '3px solid',
    borderColor: 'divider',
    opacity: 0.95,
  },
  '& hr': {
    border: 'none',
    borderTop: '1px solid',
    borderColor: 'divider',
    margin: '0.65em 0',
  },
  '& strong': { fontWeight: 700 },
  '& pre': {
    margin: '0.5em 0',
    padding: '8px 10px',
    borderRadius: '8px',
    overflowX: 'auto',
    maxWidth: '100%',
    backgroundColor: 'rgba(0,0,0,0.06)',
    fontSize: '11px',
  },
  '& code': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: '0.92em',
    padding: '0.1em 0.35em',
    borderRadius: '4px',
    backgroundColor: 'rgba(0,0,0,0.06)',
  },
  '& pre code': {
    padding: 0,
    backgroundColor: 'transparent',
  },
};

const markdownComponents: Components = {
  // Do not spread react-markdown props onto MUI Box (ref/type conflicts with component="a").
  a: ({ href, children, className }) =>
    href ? (
      <Link
        href={href}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
        underline="always"
        sx={{
          color: 'primary.main',
          wordBreak: 'break-word',
        }}
      >
        {children}
      </Link>
    ) : (
      <Box component="span" className={className} sx={{ wordBreak: 'break-word' }}>
        {children}
      </Box>
    ),
  table: ({ children }) => (
    <Box
      sx={{
        overflowX: 'auto',
        maxWidth: '100%',
        my: 0.75,
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <Box
        component="table"
        sx={{
          borderCollapse: 'collapse',
          width: '100%',
          minWidth: { xs: '100%', sm: 'max-content' },
          tableLayout: { xs: 'fixed', sm: 'auto' },
          fontSize: { xs: '10px', sm: '10.5px', md: '11px' },
          '& th, & td': {
            border: '1px solid rgba(0,0,0,0.12)',
            padding: { xs: '3px 5px', sm: '4px 7px', md: '4px 8px' },
            textAlign: 'left',
            verticalAlign: 'top',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          },
          '& th': {
            fontWeight: 700,
            backgroundColor: 'rgba(0,0,0,0.04)',
          },
        }}
      >
        {children}
      </Box>
    </Box>
  ),
};

/**
 * Renders assistant/user message bodies as GitHub-flavored Markdown (tables, lists, etc.).
 */
export default function ChatMessageMarkdown({ content }: ChatMessageMarkdownProps) {
  return (
    <Box
      className="chat-message-md"
      sx={{
        ...baseMarkdownSx,
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {content}
      </ReactMarkdown>
    </Box>
  );
}
